import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, interval, of } from 'rxjs';
import { switchMap, takeWhile, catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface PageAnalysisMetadata {
  dataSource: 'database' | 'api';
  lastUpdated?: string;
  taskId?: string;
}

export interface PageComparisonResult {
  status: 'completed' | 'pending';
  analysis?: any;
  taskId?: string | null;
  metadata: PageAnalysisMetadata;
}

export interface ComparisonResponse {
  success: boolean;
  data: {
    yourPage: PageComparisonResult;
    competitorPage: PageComparisonResult;
  };
  timestamp: string;
}

export interface AnalysisStatusResponse {
  success: boolean;
  data: {
    status: 'completed' | 'pending' | 'failed';
    analysis?: any;
    progress?: {
      pagesCrawled: number;
      pagesInQueue: number;
    };
    isInProgress?: boolean;
    error?: string;
    message?: string;
    metadata?: PageAnalysisMetadata;
  };
  timestamp: string;
}

export interface ComparisonResourceResponse {
  success: boolean;
  data: {
    execution: {
      id: string;
      yourPageUrl: string;
      competitorPageUrl: string;
      mode: string;
      steps: Array<{ step: string; status: 'pending' | 'in-progress' | 'completed' | 'error' }>;
      totalCost: number;
      startedAt: string;
      completedAt: string | null;
      error: string | null;
    };
    yourPage: {
      url: string;
      analysis: any | null;
      metadata: {
        dataSource: 'database' | null;
        lastUpdated: string | null;
      };
    };
    competitorPage: {
      url: string;
      analysis: any | null;
      metadata: {
        dataSource: 'database' | null;
        lastUpdated: string | null;
      };
    };
  };
  timestamp: string;
}

export interface IncomingLink {
  sourceUrl: string;
  sourcePageTitle?: string;
  anchorText: string;
  linkType: 'structural' | 'contextual' | 'same_page' | 'unknown';
  confidence: number;
  dofollow: boolean;
  isBroken: boolean;
  targetUrl: string;
  targetRelativeUrl: string;
}

export interface IncomingLinksResponse {
  success: boolean;
  data: {
    targetUrl: string;
    incomingLinks: IncomingLink[];
    metadata: {
      totalIncomingLinks: number;
      contextualLinks: number;
      structuralLinks: number;
      samePageLinks: number;
      unknownLinks: number;
      requiresDomainCrawl: boolean;
      /** Pages of this domain in the workspace, including the target itself. */
      pagesAnalyzedInDomain?: number;
      /**
       * Pages that could possibly link to the target — the domain minus the
       * target. 0 means there was nothing to look at, so `totalIncomingLinks: 0`
       * is the absence of a measurement rather than a measurement of zero.
       */
      linkSourcePagesInDomain?: number;
      domain: string;
    };
  };
  timestamp: string;
}

// Domain Topic Authority types
export type TopicTier = 'owned' | 'aspirational' | 'ambient' | 'adjacent' | 'anti' | 'unclassified';

export interface DomainTopic {
  parentTopicSlug: string;
  label: string;
  childTopicSlugs: string[];
  pageCount: number;
  authorityScore: number;
  topicTier?: TopicTier;
  coverageScore: number;
  strengthScore: number;
  focusScore: number;
}

export interface DomainTopicsResponse {
  success: boolean;
  data: {
    domain: string;
    parentTopics: DomainTopic[];
    message?: string; // When domain found but not aggregated
  };
  timestamp: string;
}

// Topic Share Coverage types (for mini Topic Share hook in Page Comparison)
export interface TopicShareCoverageDomain {
  domain: string;
  entitiesCovered: number;
  totalEntities: number;
  coveragePercentage: number;
  topUrls: Array<{ url: string; title: string; entitiesCount: number }>;
}

export interface TopicShareCoverageResponse {
  success: boolean;
  data: {
    available: boolean;
    topic?: string;
    domains?: TopicShareCoverageDomain[];
    sharedEntitiesCount?: number;
    lastUpdated?: Date;
    reason?: string;
  };
  timestamp: string;
}

@Injectable({
  providedIn: 'root'
})
export class PageComparisonService {
  private readonly API_URL = environment.apiUrl + '/v1';

  constructor(private http: HttpClient) {}

  /**
   * Compare two pages - initiates analysis for both URLs.
   * Pass `skipRankedKeywords: true` to skip Labs ranked-keyword fetch on this request (cost control).
   * Pass `forceRefreshCompetitor: true` to replace stored competitor analysis (fresh crawl + Labs).
   */
  comparePages(
    yourPageUrl: string,
    competitorPageUrl: string,
    options?: { skipRankedKeywords?: boolean; forceRefreshCompetitor?: boolean }
  ): Observable<ComparisonResponse> {
    const body: {
      yourPageUrl: string;
      competitorPageUrl: string;
      skipRankedKeywords?: boolean;
      forceRefreshCompetitor?: boolean;
    } = { yourPageUrl, competitorPageUrl };
    if (options?.skipRankedKeywords === true) {
      body.skipRankedKeywords = true;
    }
    if (options?.forceRefreshCompetitor === true) {
      body.forceRefreshCompetitor = true;
    }
    return this.http.post<ComparisonResponse>(`${this.API_URL}/urls/compare`, body);
  }

  /**
   * Get analysis status for a task (single check)
   */
  getAnalysisStatus(taskId: string): Observable<AnalysisStatusResponse> {
    return this.http.get<AnalysisStatusResponse>(`${this.API_URL}/urls/analysis-status/${taskId}`);
  }

  /**
   * Poll for analysis completion every 5 seconds
   * Returns Observable that emits when analysis is completed or failed
   */
  pollPageAnalysis(taskId: string): Observable<AnalysisStatusResponse> {
    return interval(5000).pipe(
      switchMap(() => this.getAnalysisStatus(taskId)),
      takeWhile((response) => {
        const status = response.data?.status;
        return status === 'pending';
      }, true), // inclusive - emit the last value
      catchError((error) => {
        console.error('Error polling analysis status:', error);
        return of({
          success: false,
          data: {
            status: 'failed',
            error: error.message || 'Failed to check analysis status'
          },
          timestamp: new Date().toISOString()
        } as AnalysisStatusResponse);
      })
    );
  }

  /**
   * Create a new page comparison execution log
   */
  createExecutionLog(yourPageUrl: string, competitorPageUrl: string, mode: 'dev' | 'user' = 'dev'): Observable<{ success: boolean; executionId: string; execution: any }> {
    return this.http.post<{ success: boolean; executionId: string; execution: any }>(
      `${this.API_URL}/urls/page-comparison/start`,
      { yourPageUrl, competitorPageUrl, mode }
    );
  }

  /**
   * Update a specific step in the execution log
   */
  updateExecutionLogStep(executionId: string, stepName: string, data: {
    status?: 'pending' | 'in-progress' | 'completed' | 'error';
    cost?: number;
    keywords?: string[];
    entities?: number;
    metadata?: Record<string, any>;
    error?: string;
  }): Observable<{ success: boolean; execution: any }> {
    return this.http.patch<{ success: boolean; execution: any }>(
      `${this.API_URL}/urls/page-comparison/${executionId}/step/${stepName}`,
      data
    );
  }

  /**
   * Get execution log by ID
   */
  getExecutionLog(executionId: string): Observable<{ success: boolean; execution: any }> {
    return this.http.get<{ success: boolean; execution: any }>(
      `${this.API_URL}/urls/page-comparison/${executionId}`
    );
  }

  /**
   * Get persisted comparison data for read-only replay.
   */
  getComparisonResource(executionId: string): Observable<ComparisonResourceResponse> {
    return this.http.get<ComparisonResourceResponse>(
      `${this.API_URL}/urls/page-comparison/${executionId}/resource`
    );
  }

  /**
   * Get incoming internal links for a page
   */
  getIncomingLinks(urlId: string, options?: { summaryOnly?: boolean }): Observable<IncomingLinksResponse> {
    const summary = options?.summaryOnly ? '?summary=1' : '';
    return this.http.get<IncomingLinksResponse>(`${this.API_URL}/urls/${urlId}/incoming-links${summary}`);
  }

  /**
   * Get domain topic authority data
   * Returns parentTopics with authorityScore and topicTier for matching against page topics
   */
  getDomainTopics(domain: string): Observable<DomainTopicsResponse> {
    return this.http.get<DomainTopicsResponse>(`${this.API_URL}/domains/${encodeURIComponent(domain)}/topics?compact=1`);
  }

  /**
   * Get Topic Share 3 coverage data for a topic and two domains
   * This is a lightweight lookup that checks if cached Topic Share 3 data exists.
   * Does NOT trigger new analysis - only reads cached data.
   */
  getTopicShareCoverage(topic: string, domains: string[]): Observable<TopicShareCoverageResponse> {
    return this.http.post<TopicShareCoverageResponse>(`${this.API_URL}/dataforseo/topic-share-3/coverage-lookup`, {
      topic,
      domains
    });
  }

}

